import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const PricingOptimization = ({ selectedPackage, selectedServices, services }) => {
  const pricingAnalysis = useMemo(() => {
    const selectedServiceObjects = services?.filter(s => selectedServices?.includes(s?.id));
    
    const totalServiceCost = selectedServiceObjects?.reduce((sum, service) => {
      return sum + (parseFloat(service?.unitPrice) || 0);
    }, 0);

    const packagePrice = selectedPackage ? parseFloat(selectedPackage?.finalPrice) || 0 : 0;
    const margin = packagePrice - totalServiceCost;
    const marginPercentage = packagePrice > 0 ? (margin / packagePrice) * 100 : 0;

    return {
      totalServiceCost,
      packagePrice,
      margin,
      marginPercentage,
      serviceCount: selectedServiceObjects?.length,
      averageServiceCost: selectedServiceObjects?.length > 0 
        ? totalServiceCost / selectedServiceObjects?.length 
        : 0
    };
  }, [selectedPackage, selectedServices, services]);

  const formatAmount = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    })?.format(value || 0);
  };

  const getMarginStatus = (percentage) => {
    if (percentage >= 30) return { color: 'text-success', bg: 'bg-success/10', label: 'Excellent' };
    if (percentage >= 20) return { color: 'text-primary', bg: 'bg-primary/10', label: 'Bon' };
    if (percentage >= 10) return { color: 'text-warning', bg: 'bg-warning/10', label: 'Acceptable' };
    return { color: 'text-error', bg: 'bg-error/10', label: 'Faible' };
  };

  const marginStatus = getMarginStatus(pricingAnalysis?.marginPercentage);

  return (
    <div className="bg-card rounded-lg border border-border shadow-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon name="TrendingUp" size={24} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Optimisation des Prix</h2>
          <p className="text-sm text-muted-foreground">
            Analyse en temps réel de la rentabilité
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="p-4 rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Package" size={18} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Prix du Package</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {formatAmount(pricingAnalysis?.packagePrice)}
          </p>
        </div>

        <div className="p-4 rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Briefcase" size={18} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Coût Total Services</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {formatAmount(pricingAnalysis?.totalServiceCost)}
          </p>
        </div>

        <div className={`p-4 rounded-lg ${marginStatus?.bg}`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={18} className={marginStatus?.color} />
            <span className={`text-sm ${marginStatus?.color}`}>Marge Bénéficiaire</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className={`text-2xl font-bold ${marginStatus?.color}`}>
              {formatAmount(pricingAnalysis?.margin)}
            </p>
            <span className={`text-sm font-medium ${marginStatus?.color}`}>
              ({pricingAnalysis?.marginPercentage?.toFixed(1)}%)
            </span>
          </div>
          <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${marginStatus?.bg} ${marginStatus?.color}`}>
            {marginStatus?.label}
          </span>
        </div>

        <div className="p-4 rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BarChart3" size={18} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Coût Moyen/Service</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {formatAmount(pricingAnalysis?.averageServiceCost)}
          </p>
          <span className="text-xs text-muted-foreground">
            {pricingAnalysis?.serviceCount} service(s) sélectionné(s)
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
          <span className="text-sm text-muted-foreground">Nombre de services</span>
          <span className="font-semibold text-foreground">{pricingAnalysis?.serviceCount}</span>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
          <span className="text-sm text-muted-foreground">Prix unitaire moyen</span>
          <span className="font-semibold text-foreground">
            {formatAmount(pricingAnalysis?.averageServiceCost)}
          </span>
        </div>

        {selectedPackage && (
          <>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
              <span className="text-sm text-muted-foreground">Unités incluses</span>
              <span className="font-semibold text-foreground">
                {selectedPackage?.includedUnits}
              </span>
            </div>

            {selectedPackage?.additionalUnitPrice && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
                <span className="text-sm text-muted-foreground">Prix unitaire additionnel</span>
                <span className="font-semibold text-foreground">
                  {formatAmount(selectedPackage?.additionalUnitPrice)}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} className="text-primary mt-0.5" />
          <div>
            <h3 className="font-semibold text-foreground mb-1">Recommandations</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              {pricingAnalysis?.marginPercentage < 20 && (
                <li>• Envisagez d'augmenter le prix du package ou de réduire les services inclus</li>
              )}
              {pricingAnalysis?.serviceCount === 0 && (
                <li>• Sélectionnez des services pour calculer la rentabilité</li>
              )}
              {pricingAnalysis?.marginPercentage >= 30 && (
                <li>• Excellente marge bénéficiaire - package bien optimisé</li>
              )}
              {pricingAnalysis?.serviceCount > 10 && (
                <li>• Nombre élevé de services - vérifiez la cohérence du package</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingOptimization;